import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Input, Skeleton, Space, Typography } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { referralActivityQueryKey, referralSummaryQueryKey, useReferralSummary } from '@/hooks/useReferralSummary';
import { referralService } from '@/services/database';
import { PlatformAlert } from '@/shared/components/alerts';
import AnalyticsSectionCard from '@/shared/components/cards/AnalyticsSectionCard';
import PlatformForm from '@/shared/components/forms/PlatformForm';
import { InvitationDraftList, InvitationEmailComposer } from '@/shared/components/invitations';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { checkForValidEmailAddress } from '@/utilities';
import {
    TbArrowUpRight,
    TbCheck,
    TbCopy,
    TbGift,
    TbLink,
    TbMailPlus,
    TbPlus,
    TbRefresh,
    TbSend,
    TbShare,
} from '@/vendor/react-icons-tb';

import { copyReferralLinkToClipboard } from '../actions';

export interface ReferralShareCardProps {
    description?: string;
    id?: string;
    showInvitationCard?: boolean;
    title?: string;
}

type ReferralLinkAction = 'copy' | 'share' | null;

const getCommissionCopy = (commissionPercent: number, commissionDurationMonths: number, appliesTo: string) => {
    const plans = appliesTo === 'monthly_and_annual' ? 'monthly and annual plan payments' : 'eligible plan payments';
    const exampleCommission = new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' }).format(
        (499 * commissionPercent) / 100,
    );

    return `Earn ${commissionPercent}% of ${plans} for up to ${commissionDurationMonths} months after each referred customer's first paid transaction. For example, a $499 eligible payment generates ${exampleCommission} in gross commission`;
};

export const ReferralLinkCard: FC<ReferralShareCardProps> = ({
    description = 'Share your personal link. You earn commission only when a referred customer purchases an eligible plan.',
    id,
    title = 'Refer New Customers',
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { data: summary, isError, isLoading, refetch } = useReferralSummary('all');
    const [activeAction, setActiveAction] = useState<ReferralLinkAction>(null);
    const [copied, setCopied] = useState(false);
    const actionInFlightRef = useRef(false);
    const copiedResetTimerRef = useRef<number>();
    const referralUrl = summary?.partner.referralUrl ?? '';
    const terms = summary?.program.terms;
    const commissionCopy = useMemo(
        () =>
            terms
                ? getCommissionCopy(terms.commissionPercent, terms.commissionDurationMonths, terms.appliesTo)
                : 'Earn commission on eligible paid subscriptions',
        [terms],
    );
    const canShare = typeof navigator.share === 'function';
    const copying = activeAction === 'copy';
    const sharing = activeAction === 'share';
    const actionsDisabled = !referralUrl || activeAction !== null;

    useEffect(
        () => () => {
            if (copiedResetTimerRef.current) {
                window.clearTimeout(copiedResetTimerRef.current);
            }
        },
        [],
    );

    const copyReferralLink = async () => {
        if (!referralUrl || actionInFlightRef.current) {
            return;
        }

        actionInFlightRef.current = true;
        setActiveAction('copy');
        setCopied(false);

        if (copiedResetTimerRef.current) {
            window.clearTimeout(copiedResetTimerRef.current);
        }

        try {
            const didCopy = await copyReferralLinkToClipboard(referralUrl);

            if (didCopy) {
                setCopied(true);
                copiedResetTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
            }
        } finally {
            actionInFlightRef.current = false;
            setActiveAction(null);
        }
    };

    const shareReferralLink = async () => {
        if (!referralUrl || !canShare || actionInFlightRef.current) {
            return;
        }

        actionInFlightRef.current = true;
        setActiveAction('share');

        try {
            await navigator.share({
                text: 'Join Countercyclical with my referral link. If you become a paying customer, I may earn a commission.',
                title: 'Countercyclical referral invitation',
                url: referralUrl,
            });
        } catch (error) {
            if (!(error instanceof DOMException && error.name === 'AbortError')) {
                captureException(error, { tags: { operation: 'referral.share-link' } });
                toast.error('Unable to share referral link', {
                    description: 'Copy the link and share it from your preferred app.',
                });
            }
        } finally {
            actionInFlightRef.current = false;
            setActiveAction(null);
        }
    };

    return (
        <AnalyticsSectionCard
            ariaLabel='Refer new customers'
            contentStyle={{ padding: '1.125rem' }}
            description={description}
            icon={<TbLink />}
            id={id}
            title={title}
        >
            <div aria-busy={isLoading}>
                {isLoading ? (
                    <Skeleton active paragraph={{ rows: 2 }} title={false} />
                ) : isError ? (
                    <div role='alert'>
                        <Typography.Text type='secondary'>Your referral link could not be loaded.</Typography.Text>
                        <Button
                            icon={<TbRefresh size='1rem' />}
                            onClick={() => void refetch()}
                            style={{ marginLeft: '0.75rem' }}
                        >
                            Try again
                        </Button>
                    </div>
                ) : (
                    <>
                        {terms ? (
                            <PlatformAlert
                                description={`${commissionCopy}. Signups and free accounts do not generate commission.`}
                                icon={<TbGift />}
                                showIcon
                                status='success'
                                styles={{ marginBottom: '1rem' }}
                                title='Affiliate Commission'
                            />
                        ) : null}
                        <PlatformForm layout='vertical'>
                            <PlatformForm.Item
                                htmlFor='personal-referral-link'
                                label='Referral Link'
                                style={{ marginBottom: 0 }}
                            >
                                <Input
                                    addonBefore={
                                        <span
                                            aria-hidden='true'
                                            style={{
                                                alignItems: 'center',
                                                display: 'inline-flex',
                                                justifyContent: 'center',
                                                lineHeight: 0,
                                                marginInline: '-0.25rem',
                                            }}
                                        >
                                            <TbLink size='1rem' style={{ color: token.colorTextQuaternary }} />
                                        </span>
                                    }
                                    aria-label='Personal referral link'
                                    id='personal-referral-link'
                                    onFocus={(event) => event.currentTarget.select()}
                                    readOnly
                                    value={referralUrl}
                                />
                            </PlatformForm.Item>
                        </PlatformForm>
                        <Divider style={{ margin: '1rem 0' }} />
                        <div
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '0.75rem',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Space.Compact>
                                <Button
                                    disabled={actionsDisabled}
                                    icon={copied ? <TbCheck size='1rem' /> : <TbCopy size='1rem' />}
                                    loading={copying}
                                    onClick={() => void copyReferralLink()}
                                >
                                    {copied ? 'Copied' : 'Copy Referral Link'}
                                </Button>
                                {canShare ? (
                                    <Button
                                        disabled={actionsDisabled}
                                        icon={<TbShare size='1rem' />}
                                        loading={sharing}
                                        onClick={() => void shareReferralLink()}
                                        type='primary'
                                    >
                                        Share Invitation
                                    </Button>
                                ) : null}
                            </Space.Compact>
                            {terms ? (
                                <Typography.Link href={terms.policyUrl} rel='noopener noreferrer' target='_blank'>
                                    Program Terms
                                    <TbArrowUpRight />
                                </Typography.Link>
                            ) : null}
                        </div>
                    </>
                )}
            </div>
        </AnalyticsSectionCard>
    );
};

export const ReferralInvitationCard: FC<Pick<ReferralShareCardProps, 'id'>> = ({ id }): JSX.Element => {
    const queryClient = useQueryClient();
    const [invitationDrafts, setInvitationDrafts] = useState<string[]>([]);
    const [invitationEmailAddress, setInvitationEmailAddress] = useState('');
    const invitationMutation = useMutation({
        mutationFn: (emailAddresses: string[]) => referralService.createInvitations(emailAddresses),
    });
    const normalizedInvitationEmailAddress = invitationEmailAddress.trim().toLowerCase();
    const invitationAlreadyAdded = invitationDrafts.includes(normalizedInvitationEmailAddress);
    const canAddInvitation =
        checkForValidEmailAddress(normalizedInvitationEmailAddress) &&
        !invitationAlreadyAdded &&
        invitationDrafts.length < 10 &&
        !invitationMutation.isPending;

    const addReferralInvitation = () => {
        if (!canAddInvitation) {
            return;
        }

        setInvitationDrafts((current) => [...current, normalizedInvitationEmailAddress]);
        setInvitationEmailAddress('');
    };

    const sendReferralInvitations = async () => {
        try {
            const result = await invitationMutation.mutateAsync(invitationDrafts);

            void queryClient.invalidateQueries({ queryKey: referralSummaryQueryKey });
            void queryClient.invalidateQueries({ queryKey: referralActivityQueryKey('invitations') });

            if (result.failedCount) {
                setInvitationDrafts(
                    result.invitations
                        .filter(({ status }) => status === 'Failed')
                        .map(({ emailAddress }) => emailAddress),
                );
                toast.warning('Some referral invitations were not delivered', {
                    description: `${result.sentCount} sent · ${result.failedCount} failed. You can retry failed invitations from this form.`,
                });
            } else {
                toast.success('Referral invitations sent', {
                    description: `${result.sentCount} invitation${result.sentCount === 1 ? '' : 's'} delivered.`,
                    icon: <TbMailPlus size='1rem' />,
                });
                setInvitationDrafts([]);
            }
        } catch (error) {
            captureException(error, { tags: { operation: 'referral.send-invitations' } });
            toast.error('Unable to send referral invitations', {
                description: error instanceof Error ? error.message : 'Copy your referral link and try again later.',
            });
        }
    };

    return (
        <AnalyticsSectionCard
            ariaLabel='Send referral invitations'
            contentStyle={{ padding: '1.125rem' }}
            description='Send referral invitations to up to 10 recipients at a time. Each recipient receives your personal link.'
            icon={<TbMailPlus />}
            id={id}
            title='Send Referral Invitations'
        >
            <InvitationEmailComposer
                buttonDisabled={!canAddInvitation}
                buttonIcon={<TbPlus size='1rem' />}
                buttonLabel='Add Referral Invitation'
                disabled={invitationMutation.isPending}
                emailAddress={invitationEmailAddress}
                inputId='referral-invitation-email-address'
                onEmailAddressChange={setInvitationEmailAddress}
                onSubmit={addReferralInvitation}
            />
            <InvitationDraftList
                countLabel={`${invitationDrafts.length} Referral Invitation${invitationDrafts.length === 1 ? '' : 's'} to Send`}
                description={() => 'Receives your personal referral link'}
                emptyState={{
                    description: 'Add contacts above to prepare their personal referral invitations.',
                    icon: <TbMailPlus size='1rem' />,
                    label: 'No Referral Invitations Added',
                }}
                footer={
                    <Button
                        block
                        disabled={invitationDrafts.length === 0}
                        icon={<TbSend size='1rem' />}
                        loading={invitationMutation.isPending}
                        onClick={() => void sendReferralInvitations()}
                        style={{ marginTop: '0.75rem' }}
                        type='primary'
                    >
                        Send Invitations
                    </Button>
                }
                getKey={(emailAddress) => emailAddress}
                items={invitationDrafts}
                onRemove={(emailAddress) =>
                    setInvitationDrafts((current) => current.filter((candidate) => candidate !== emailAddress))
                }
                removeDisabled={invitationMutation.isPending}
                removeLabel={(emailAddress) => `Remove ${emailAddress}`}
                title={(emailAddress) => emailAddress}
            />
        </AnalyticsSectionCard>
    );
};

const ReferralShareCard: FC<ReferralShareCardProps> = ({ showInvitationCard = true, ...props }): JSX.Element => (
    <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
        <ReferralLinkCard {...props} />
        {showInvitationCard ? <ReferralInvitationCard /> : null}
    </div>
);

export default ReferralShareCard;
